import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import api from '../services/api';

export interface AppFeatures {
  /** QR-code check-in: ticket emails, in-app QR display, and admin QR scanning. */
  qrCheckIn: boolean;
  /** D8 — lets a family add/edit/remove their own permanent family members. */
  selfAddMembers: boolean;
}

interface ConfigContextType {
  features: AppFeatures;
  loading: boolean;
}

// Fail-safe default: features are OFF until the backend confirms them, so a
// disabled environment never momentarily shows a retired feature. The hard
// guarantee (no QR ticket emails) lives server-side regardless of this default.
const DEFAULT_FEATURES: AppFeatures = { qrCheckIn: false, selfAddMembers: false };

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [features, setFeatures] = useState<AppFeatures>(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/config')
      .then((res) => {
        if (!mounted) return;
        const f = res.data?.features ?? {};
        setFeatures({ qrCheckIn: f.qrCheckIn === true, selfAddMembers: f.selfAddMembers === true });
      })
      .catch(() => {
        // Keep fail-safe defaults (features off) on error.
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <ConfigContext.Provider value={{ features, loading }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextType => {
  const ctx = useContext(ConfigContext);
  if (ctx === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return ctx;
};

export default ConfigContext;
